import { lazy, Suspense } from 'react'
import './App.css'
import {
  BrowserRouter as Router,
  useLocation,
  useNavigate,
  matchPath,
  useSearchParams
} from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { useResponsive, useDeleteAccount, useUserJourney, useUserAnalyticsEvent, useSubscriptionStatus } from 'shared-ui/hooks'
import React, { useState, useRef, useEffect } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import {
  ScrollToTop,
  ScrollToTopDiv,
  Splash,
  ToastProvider,
  InviteModal,
  Loader,
  useToast,
  SnackBarProvider,
  CookieConsent,
  Header,
  ToolsTutorialPopup,
  QueueNotificationWidget,
  MiniAppConsentModal,
  WithdrawConsentModal
} from 'shared-ui/components'
import { useUnifiedJobStream } from 'shared-ui/hooks'
import AppRoutes from './routes/appRoutes.jsx'
import { media } from 'shared-ui/utils/media.js'
import isAuthRoute from 'shared-ui/utils/helper.jsx'
import Theme from './Theme.jsx'
import { useSplashScreen } from 'shared-ui/hooks/useSplashScreen.js'
import { getSubscriptionStatus } from 'shared-ui/redux/reducers/statusReducer'
import { clearApiGetCache } from 'shared-ui/api'
import {
  NOTIFICATION_COUNT,
  PROFILE_BADGE,
  SPLASH_SESSION_KEY,
  SIDE_POPUP,
  ROUTES,
  NOTIFICATION_TEXT,
  DELETE_ACCOUNT_TEXT,
  BLOCKED_USER_TEXT,
  NEW_SIDE_POPUP, BUTTON_LABELS,
  ERROR_CODES,
  HIDE_HEADER_ROUTES,
  MOBILE_HIDDEN_ROUTES,
  HIDE_SIDEBAR_ROUTES,
  TUTORIAL_VIDEO_URLS,
  getDynamicVideoUrls
} from 'shared-ui/utils/constants'

import {
  setGuestUser,
  clearNotificationSetting,
  getGuestUser
} from 'shared-ui/utils/localStorage.js'
import { isPreviewMode, lockPreviewInteractions } from 'shared-ui/utils/previewMode'
import { logout as logoutAction } from 'shared-ui/redux/reducers/loginReducer.jsx'
import { clearAllJobs } from 'shared-ui/redux/reducers/jobsReducer.jsx'
import { unifiedJobStreamService } from 'shared-ui/services/unifiedJobStreamService'
import { AppThemeProvider } from 'shared-ui/utils/themeProvider.js'

import { useLogout } from 'shared-ui/hooks/useLogin.js'
import { AnalyticsTracker } from 'shared-ui/utils/AnalyticsTracker'
import { GA_EVENTS,CMS_EVENTS } from 'shared-ui/utils/gaConstants'
import { useTrackEvent } from 'shared-ui/utils/analyticEvents'
import { useFirebaseMessaging } from 'shared-ui/hooks/useFireBaseMessaging'
import { LanguageProvider } from 'shared-ui/utils/languageProvider.js'
import { useDeviceRegistration, usePushTokenSync, useNetworkState, useFetchInviteAPI } from 'shared-ui/hooks'

function CookieBanner() {

  const location = useLocation()
  const params = new URLSearchParams(location.search)
  const isAndroid = params.get('source') === 'ANDROID' || location.state?.source === 'ANDROID'
  if (isAndroid) return null
  return <CookieConsent />
}
const Sidebar = lazy(() => import('shared-ui/components/Sidebar'))

const RightSidePanel = lazy(() => import('shared-ui/components/RightSidePanel'))

const Search = lazy(() => import('./pages/Search'))
const UpdateEmail = lazy(() => import('./pages/Setting/UpdateEmail'))
const UpdatePassword = lazy(() => import('./pages/Setting/UpdatePassword'))
const LogoutModal = lazy(() => import('./pages/Setting/LogoutModal'))
const LogoutSuccessModal = lazy(
  () => import('./pages/Setting/LogoutSuccessModal')
)
const Notifications = lazy(() => import('./pages/Notification'))
const Settings = lazy(() => import('./pages/Setting'))
const ThemePage = lazy(() => import('./pages/Setting/ThemePage'))
const LanguagePage = lazy(() => import('./pages/Setting/LanguagePage'))
const DeleteAccount = lazy(() => import('./pages/DeleteAccount'))
const BlockUserList = lazy(() => import('shared-ui/components/BlockUserList'))

const DeleteSuccessModal = lazy(
  () => import('./pages/DeleteAccount/DeleteSuccessModal')
)
const NotificationPage = lazy(
  () => import('./pages/Setting/NotificationPage.jsx')
)
// const FirebaseMessagingLoader = lazy(() => import('./FirebaseMessagingLoader'))
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      refetchOnReconnect: false,
      retry: 2
    }
  }
})

if (typeof window !== 'undefined') {
  window.__QUERY_CLIENT__ = queryClient
}

const AppContent = () => {
  const [showSplash, setShowSplash] = useSplashScreen()
  const isAuthenticated = useSelector((state) => state.auth.isAuthenticated)
  const user = useSelector((state) => state.auth.user)
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const location = useLocation()
  const deviceId = localStorage.getItem('deviceId')
  const { logCmsEvent } = useUserAnalyticsEvent()
  const sessionStartRef = useRef(Date.now());
  const params = new URLSearchParams(location.search)
  const isAndroid = params.get('source') === 'ANDROID' || location.state?.source === 'ANDROID'
  const [searchParams] = useSearchParams()
  const wellnessPanel = searchParams.get('panel')
  const [isComponentOpen, setIsComponentOpen] = useState(false)
  const [selectedComponent, setSelectedComponent] = useState(null)
  const [showUpdateEmailModal, setShowUpdateEmailModal] = useState(false)
  const [showUpdatePasswordModal, setShowUpdatePasswordModal] = useState(false)
  const [showThemeModal, setShowThemeModal] = useState(false)
  const [showLanguageModal, setShowLanguageModal] = useState(false)

  const [showLogoutModal, setShowLogoutModal] = useState(false)
  const [showLogoutSuccessModal, setShowLogoutSuccessModal] = useState(false)
  const [showRightPanel, setShowRightPanel] = useState(false)
  const [rightPanelTitle, setRightPanelTitle] = useState('')
  const [rightPanelContent, setRightPanelContent] = useState(null)
  const [showDeleteSuccessModal, setShowDeleteSuccessModal] = useState(false)
  const [showNoInternetModal, setShowNoInternetModal] = useState(false);
  const [showNoInteretModalData, setShowNoInteretModalData] = useState({});
  const [showLoader, setShowLoader] = useState(false)
  const [deleteConfirmationData, setDeleteConfirmationData] = useState(false)
  const [showInfoModal,setShowInfoModal]=useState(false)
  const { showError } = useToast()
  const isMobile = useResponsive(media.mobile)
  const trackEvent = useTrackEvent()
  const network = useNetworkState();
  const howItWorks = useSelector((state) => state.homeData.howItWorks)
  const howItWorksVideo = getDynamicVideoUrls(howItWorks, 'HOME', isMobile) || (isMobile
      ? TUTORIAL_VIDEO_URLS.MOBILE.HOME
      : TUTORIAL_VIDEO_URLS.DESKTOP.HOME)
  useFirebaseMessaging(isAuthenticated)
  const { data: statusData } = useSubscriptionStatus()   
  const fcmToken =
    useSelector((state) => state.auth?.fcmToken) ||
    localStorage.getItem('fcmToken');

  // useDeviceRegistration({
  //   enabled: !!fcmToken
  // });
  const { isFetching: isRegisteringDevice } = useDeviceRegistration()
  usePushTokenSync({ enabled: !isRegisteringDevice })
  // InviteRouteGuard sits inside the Suspense boundary below, so its invite query would only start
  // once the lazy shell chunks load. Subscribing here starts the same query on first render.
  useFetchInviteAPI()
  useUnifiedJobStream(true)
  const {sendUserJourney} = useUserJourney();
  const { isOnline } = network;
  const retryTimeoutRef = useRef(null);
  const notificationTimeoutRef = useRef(null);

  const hideSideNav = [ROUTES.terms, ROUTES.privacy, ROUTES.paymentFailed].includes(location.pathname)

  useEffect(() => {
    if (statusData) {
      localStorage.setItem('subscriptionStatus', JSON.stringify(statusData))
    }
  }, [statusData])

  useEffect(() => {
    const sendSessionStart = () => {
      sessionStartRef.current = Date.now();
      sendUserJourney( CMS_EVENTS.SESSION_START,{
        platform: CMS_EVENTS.PLATFORM,
        screenName: location.pathname,
      });
       logCmsEvent(CMS_EVENTS.SESSION_START);
    };

    // Initial app launch
    sendSessionStart();

    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        sendSessionStart();
      }
      if(document.visibilityState === "hidden"){
        const duration = Date.now() - sessionStartRef.current;
        logCmsEvent(CMS_EVENTS.SESSION_END,{
          duration
        })
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener(
        "visibilitychange",
        handleVisibilityChange
      );
    };
  }, []);
const isMobileHiddenRoute =
  isMobile && 
  MOBILE_HIDDEN_ROUTES.some((route) =>
    location.pathname.includes(route)
  )


const hideHeader = HIDE_HEADER_ROUTES.some((route) =>
  matchPath(route, location.pathname)
) || isMobileHiddenRoute

  const handleAppLogoutCleanup = async () => {
      dispatch(logoutAction())

      const keysToRemove = [
        'firstTimeUser',
        'access',
        'refresh',
        'user',
        'access_expiry',
        'guidelinesShown',
        'exploreReportClicked',
        'moreReportsClicked',
        'isSocialUser',
        'mini_app_consents'
      ]
      keysToRemove.forEach((key) => localStorage.removeItem(key))
    setGuestUser(true)

      clearNotificationSetting()
      clearApiGetCache()

      await queryClient.cancelQueries()
      queryClient.clear()

    setIsComponentOpen(false)
    setSelectedComponent(null)
  }

  const {
    mutate: logoutMutate,
    isLoading: isLogoutLoading,
    isPending: isLogoutPending
  } = useLogout({
    onSuccess: async () => {
      await handleAppLogoutCleanup()

      if (isMobile) {
        navigate('/logout', { replace: true })
      } else {
        setShowLogoutSuccessModal(true)
      }
    },

    onError: (error) => {
      console.error('Logout failed on server:', error)
      const message =
        error?.response?.data?.message ||
        error?.message ||
        'Logout failed. Please try again.'
      showError?.(message)
    }
  })
  const isLoggingOut = Boolean(isLogoutLoading || isLogoutPending)

  const deleteAccountMutation = useDeleteAccount({
    onSuccess: async () => {
      await handleAppLogoutCleanup()

      if (isMobile) {
        navigate(ROUTES.DeleteAccount, { replace: true })
      } else {
        setShowDeleteSuccessModal(true)
        setDeleteConfirmationData(false)
      }
    },
    onError: async (error) => {
      console.error('Delete account failed:', error)
      await handleAppLogoutCleanup()

      if (isMobile) {
        navigate(ROUTES.DeleteAccount, { replace: true })
      } else {
        setShowDeleteSuccessModal(true)
        setDeleteConfirmationData(false)
      }
    }
  })

  const deleteAccount = () => {
    deleteAccountMutation.mutate()
  }
  const isGuest =
    getGuestUser() === 'true' &&
    !isAuthRoute(location.pathname) &&
    !matchPath('/', location.pathname) &&
    !isAuthenticated

  const isLoginOrRegister =
    isAuthRoute(location.pathname.toLowerCase()) || !isAuthenticated

  const sidebarPopupRef = useRef(null)
  const mainScrollRef = useRef(null)

  const hideScroll =
    !isMobile &&
    [
      '/ai-tool',
      '/discover'
    ].some((path) => location.pathname.includes(path));

  useEffect(() => {
    if (isAuthenticated) {
      dispatch(getSubscriptionStatus())
    }
  }, [isAuthenticated])

  useEffect(() => {
    if (wellnessPanel === 'settings') {
      setSelectedComponent('More');
      setIsComponentOpen(true)
    } else {
      setIsComponentOpen(false)
      setSelectedComponent(null)
    }
  }, [location.pathname, wellnessPanel])

  const handleOpenUpdateEmailModal = () => {
    setShowUpdateEmailModal(true)
  }

  // Handler for closing update email modal
  const handleCloseUpdateEmailModal = () => {
    setShowUpdateEmailModal(false)
  }

  // Handlers for Update Password modal
  const handleOpenUpdatePasswordModal = () => {
    setShowUpdatePasswordModal(true)
  }

  const handleCloseUpdatePasswordModal = () => {
    setShowUpdatePasswordModal(false)
  }

  const handleOpenThemeModal = () => setShowThemeModal(true)
  const handleCloseThemeModal = () => setShowThemeModal(false)
  const handleOpenLanguageModal = () => setShowLanguageModal(true)
  const handleCloseLanguageModal = () => setShowLanguageModal(false)

  // Handlers for Logout modal (desktop sidebar)
  const handleOpenLogoutModal = () => setShowLogoutModal(true)
  const handleCloseLogoutModal = () => {
    setShowLogoutModal(false)
    setDeleteConfirmationData(false)
  }

  const handleConfirmLogout = () => {
    if (deleteConfirmationData) {
      deleteAccount()
    } else {
      logoutMutate()
      trackEvent(GA_EVENTS.logout_confirmed)
    }

    setShowLogoutModal(false) // Close confirmation modal
  }

  // Right side panel openers
  const handleOpenDeleteAccountPanel = () => {
    setRightPanelTitle(DELETE_ACCOUNT_TEXT?.TITLE)
    setRightPanelContent(
      <DeleteAccount
        isModal={true}
        onClose={() => {
          setShowRightPanel(false)
          setDeleteConfirmationData(false)
        }}
        onDeleted={() => {
          setDeleteConfirmationData(true)
          setShowRightPanel(false)
          setShowLogoutModal(true)
        }}
      />
    )
    setShowRightPanel(true)
  }

  const handleOpenBlockedUserList = () => {
    setRightPanelTitle(BLOCKED_USER_TEXT?.TITLE)
    setRightPanelContent(
      <BlockUserList isModal={true} />
    )
    setShowRightPanel(true)

  }
  const handleOpenFeedbackPanel = () => {
    // setRightPanelTitle('Feedback & Support')
    // setRightPanelContent(
    //   <SubmitFeedback isModal={true} onClose={() => setShowRightPanel(false)} />
    // )
    // setShowRightPanel(true)
  }

  const handleOpenNotificationPanel = () => {
    setRightPanelTitle(NOTIFICATION_TEXT.Notifications)
    setRightPanelContent(
      <NotificationPage
        isModal={true}
        onClose={() => setShowRightPanel(false)}
      />
    )
    setShowRightPanel(true)
  }

  const handleOpenNotificationsListPanel = () => {
    setRightPanelTitle(NEW_SIDE_POPUP.NOTIFICATIONS)
    setRightPanelContent(
      <Notifications onClose={() => setShowRightPanel(false)} />
    )
    setShowRightPanel(true)
  }

  useEffect(() => {
    window.openNotificationsPanel = handleOpenNotificationsListPanel
    return () => {
      if (window.openNotificationsPanel === handleOpenNotificationsListPanel) {
        window.openNotificationsPanel = undefined
      }
    }
  }, [])

  useEffect(() => {
    if (isComponentOpen) {
      window.dispatchEvent(new CustomEvent('SIDEBAR_OPENED'))
    }
  }, [isComponentOpen])

  const isAnyModalOpen =
    showUpdateEmailModal ||
    showUpdatePasswordModal ||
    showThemeModal ||
    showLanguageModal ||
    showLogoutModal ||
    showLogoutSuccessModal ||
    showDeleteSuccessModal

  useEffect(() => {
    setShowUpdateEmailModal(false)
    setShowUpdatePasswordModal(false)
    setShowThemeModal(false)
    setShowLanguageModal(false)
    setShowLogoutModal(false)
    setShowLogoutSuccessModal(false)
    setShowDeleteSuccessModal(false)
  }, [location.pathname])

  useEffect(() => {
    function handleClickOutside(event) {
      if (isAnyModalOpen) return

      const clickedOnSidebar = event.target.closest('.sidebar-wrapper')

      if (
        sidebarPopupRef.current &&
        !sidebarPopupRef.current.contains(event.target) &&
        !clickedOnSidebar &&
        isComponentOpen
      ) {
        setIsComponentOpen(false)
        setSelectedComponent(null)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [isComponentOpen, isAnyModalOpen])

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search)

    // Check if this is a notification deep link
    if (searchParams.has('notification')) {
      // Import the parsing function
      import('shared-ui/utils/notificationDeeplink')
        .then(({ parseNotificationFromURL, handleNotificationDeeplink }) => {
          const notificationPayload = parseNotificationFromURL(searchParams)

          if (notificationPayload) {
            const type =
              notificationPayload.type ||
              notificationPayload.action ||
              'unknown'
            const messageId = notificationPayload.messageId || 'unknown'
            const timeSinceSent = notificationPayload.timeSinceSent || '0'

            trackEvent(GA_EVENTS.notification_clicked, {
              notification_type: type,
              campaign_id: messageId,
              time_since_sent_hours: timeSinceSent
            })

            // Small delay to ensure app is ready
            notificationTimeoutRef.current = setTimeout(() => {
              handleNotificationDeeplink(notificationPayload, navigate);

              // Clean up URL parameters after processing
              const newUrl = window.location.pathname
              window.history.replaceState({}, '', newUrl)
            }, 500)
          } else {
            console.warn('Failed to parse notification payload from URL')
          }
        })
        .catch((error) => {
          console.error('Error importing notification deeplink module:', error)
        })
    }

    return () => {
      clearTimeout(notificationTimeoutRef.current);
    };
  }, [location.search, navigate, trackEvent])

  useEffect(() => {
    if (!isOnline) {
      setShowNoInternetModal(true);
      const data = ERROR_CODES("nw00")
      const parsedData = JSON.parse(data);
      setShowNoInteretModalData(parsedData)
    }
    else {
      setShowNoInternetModal(false);
    }
  }, [isOnline]);


  const handleRetryData = (online) => {
    setShowNoInternetModal(false);
    setShowLoader(true);
    clearTimeout(retryTimeoutRef.current);
    retryTimeoutRef.current = setTimeout(() => {
      setShowLoader(false);
      if (!online) {
        setShowNoInternetModal(true);
      }
    }, 800);
  }

  useEffect(() => {
    return () => {
      clearTimeout(retryTimeoutRef.current);
    };
  }, []);
  return (
    <div className="App">
      {!showSplash && isMobile ? (
        <Suspense fallback={null}>
          <Splash
            onEnter={() => {
              sessionStorage.setItem(SPLASH_SESSION_KEY, 'false')
              setShowSplash(false)
            }}
            onCreateAcc={() => {
              sessionStorage.setItem(SPLASH_SESSION_KEY, 'false')
              setShowSplash(false)
              navigate(ROUTES.register)
            }}
            onGuestUserClick={() => {
              sessionStorage.setItem(SPLASH_SESSION_KEY, 'false')
              setShowSplash(false)
              setGuestUser(true)
              navigate(ROUTES.home)
            }}
          />
        </Suspense>
      ) : (
        <>
          <ScrollToTopDiv scrollRef={mainScrollRef} />
          <Loader show={showLoader} coverBg={true} />
          <Suspense fallback={<Loader />}>
            <div className={`flex flex-col ${isMobile ? 'h-[100dvh]' : 'h-screen'}`}>
          {!hideHeader && <Header onOpenInfoModal={() => setShowInfoModal(true)}/>}
            <div className="flex flex-1 overflow-hidden">
              <div className="h-full">
                  {deviceId && !isAndroid && <CookieConsent />}
                  {((!isLoginOrRegister &&
                    !matchPath('/selectTemplate', location.pathname)) ||
                    isGuest ||
                    isPreviewMode()) && (
                      <div
                        className="relative flex"
                        style={{
                          display: hideSideNav ? 'none' : ''
                        }}
                      >
                        <Sidebar
                          isHeaderShow={!hideHeader}
                          forceCollapsed={wellnessPanel === 'settings'}
                          notifications={NOTIFICATION_COUNT}
                          profileBadge={PROFILE_BADGE}
                          setIsComponentOpen={setIsComponentOpen}
                          setSelectedComponent={setSelectedComponent}
                          selectedComponent={selectedComponent}
                          isComponentOpen={isComponentOpen}
                          isSidebarOpen={
                            !(
                              HIDE_SIDEBAR_ROUTES.some((route) =>
                                matchPath(route, location.pathname)
                              ) ||
                              [
                                SIDE_POPUP.SEARCH,
                                SIDE_POPUP.NOTIFICATIONS,
                                SIDE_POPUP.MORE
                              ].includes(selectedComponent)

                            )
                          }
                          hoverExpandable={
                            !!matchPath('/content/:id', location.pathname)
                          }
                        />
                        <div
                          ref={sidebarPopupRef}
                          className={`${!isMobile
                            ? `fixed ${hideHeader
                              ? 'top-0 h-full'
                              : 'top-16 h-[calc(100vh-4rem)]'
                            } left-16 w-[400px] overflow-y-auto transition-transform duration-300 ease-in-out
      ${isComponentOpen ? 'translate-x-0 z-[1000]' : '-translate-x-full'}`
                            : ''
                            }`}
                          style={{
                            scrollbarWidth: 'none',
                            msOverflowStyle: 'none'
                          }}
                        >
                          <Suspense fallback={null}>
                            {selectedComponent === SIDE_POPUP.SEARCH ? (
                              <Search setSideComponentOpen={setIsComponentOpen} />
                            ) : (selectedComponent === SIDE_POPUP.MORE || wellnessPanel === 'settings') ? (
                              <Settings
                                onOpenUpdateEmailModal={handleOpenUpdateEmailModal}
                                onOpenUpdatePasswordModal={
                                  handleOpenUpdatePasswordModal
                                }
                                onOpenThemeModal={handleOpenThemeModal}
                                onOpenLanguageModal={handleOpenLanguageModal}
                                onOpenLogoutModal={handleOpenLogoutModal}
                                onCloseSidebarDrawer={() => setIsComponentOpen(false)}
                                onOpenBlockedUserListPanel={handleOpenBlockedUserList}
                                onOpenDeleteAccountPanel={
                                  handleOpenDeleteAccountPanel
                                }
                                // onOpenFeedbackPanel={handleOpenFeedbackPanel}
                                onOpenNotificationPanel={handleOpenNotificationPanel}
                                isSidebarView={true}
                              />
                            ) : null}
                          </Suspense>
                        </div>
                      </div>
                    )}
                </div>
                <div
                  ref={mainScrollRef}
                  className={`h-full ${hideScroll ? "" : "overflow-y-auto"} flex-1`}
                >
                  <AppRoutes />
                </div>
              </div>
            </div>
            {showUpdateEmailModal && !isMobile && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] app-modal"
                onClick={(e) => {
                  handleCloseUpdateEmailModal();
                  e.stopPropagation()
                }}>
                <Suspense fallback={null}>
                  <UpdateEmail
                    isModal={true}
                    onClose={handleCloseUpdateEmailModal}
                  />
                </Suspense>
              </div>
            )}
            {showUpdatePasswordModal && !isMobile && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] app-modal"
                onClick={(e) => {
                  handleCloseUpdatePasswordModal();
                  e.stopPropagation()
                }}>
                <Suspense fallback={null}>
                  <UpdatePassword
                    isModal={true}
                    onClose={handleCloseUpdatePasswordModal}
                  />
                </Suspense>
              </div>
            )}
            {showNoInternetModal &&
              <InviteModal
                header={showNoInteretModalData?.title}
                message={showNoInteretModalData?.message}
                retryButton
                confirmText={BUTTON_LABELS.retry}
                errorCode={"nw00"}
                handleRetry={() => handleRetryData(isOnline)}
                onClick={() => setShowNoInternetModal(false)}
                cancelText={BUTTON_LABELS.cancel}
              />}
              {setShowInfoModal && <ToolsTutorialPopup
                isOpen={showInfoModal}
                onClose={() => setShowInfoModal(false)}
                selectedTool={null}
                customVideoSources={howItWorksVideo}
                isHowItWorksFromHome={true}
              />}
            {showThemeModal && !isMobile && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] app-modal"
                onClick={(e) => {
                  e.stopPropagation();
                  handleCloseThemeModal()
                }}>
                <Suspense fallback={null}>
                  <ThemePage isModal={true} onClose={handleCloseThemeModal} />
                </Suspense>
              </div>
            )}
            {showLanguageModal && !isMobile && (
              <div
                className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000] app-modal"
                onClick={(e) => {
                  handleCloseLanguageModal();
                  e.stopPropagation()
                }}
              >
                <Suspense fallback={null}>
                  <LanguagePage
                    isModal={true}
                    onClose={handleCloseLanguageModal}
                  />
                </Suspense>
              </div>
            )}
            {showLogoutModal && !isMobile && (
              <LogoutModal
                deleteAccount={deleteConfirmationData}
                onClose={handleCloseLogoutModal}
                onConfirm={handleConfirmLogout}
              />
            )}
            {showLogoutSuccessModal && !isMobile && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60]">
                <Suspense fallback={null}>
                  <LogoutSuccessModal
                    isModal={true}
                    onClose={() => {
                      setShowLogoutSuccessModal(false)
                      handleAppLogoutCleanup()
                    }}
                  />
                </Suspense>
              </div>
            )}

            {showDeleteSuccessModal && !isMobile && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[60] ">
                <Suspense fallback={null}>
                  <DeleteSuccessModal
                    isModal={true}
                    onClose={() => setShowDeleteSuccessModal(false)}
                  />
                </Suspense>
              </div>
            )}
            {!isMobile && (
              <RightSidePanel
                open={showRightPanel}
                title={rightPanelTitle}
                onClose={() => setShowRightPanel(false)}
                width={'480px'}
              >
                <Suspense fallback={null}>
                  {rightPanelContent}
                </Suspense>
              </RightSidePanel>
            )}
            <QueueNotificationWidget />
            <Loader show={isLoggingOut} coverBg={true} />
            <MiniAppConsentModal />
            <WithdrawConsentModal />
          </Suspense>
        </>
      )}
      {!isOnline && (
        <div className="fixed inset-0 z-[9999] bg-transparent" onClick={() => setShowNoInternetModal(true)}></div>
      )}

    </div>
  )
}

function App() {
  window.version = '1.3'
  useEffect(() => lockPreviewInteractions(), [])
  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider>
        <Theme>
          <AppThemeProvider>
            <Router>
              <ToastProvider>
                <SnackBarProvider>
                  <AppContent />
                  <AnalyticsTracker />
                  {/* <CookieBanner /> */}
                </SnackBarProvider>
              </ToastProvider>
            </Router>

            {/* <ReactQueryDevtools initialIsOpen={false} />  */}
          </AppThemeProvider>
        </Theme>
      </LanguageProvider>
    </QueryClientProvider>
  )
}

export default App
